import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { StatCard } from '../../components/ui/StatCard'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { BarChart } from '../../components/charts/BarChart'
import { formatEuro, formatDataOra } from '../../lib/format'
import pagina from '../pagine.module.css'

interface Revenue {
  mrr: number
  arr: number
  per_piano: Array<{ piano: string; utenti: number; revenue: number }>
  ultimi_pagamenti: Array<{ importo: number; data: string; stato: string }>
  prossimi_rinnovi: Array<{ data: string | null }>
  churn_ultimi_30_giorni: unknown[]
}

export function AdminRevenue() {
  const q = useQuery({
    queryKey: ['admin', 'revenue'],
    queryFn: async () => (await api.get<{ dati: Revenue }>('/superadmin/revenue')).data.dati,
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento revenue da Stripe…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento Stripe" descrizione="Verifica STRIPE_SECRET_KEY in .env" />

  const r = q.data
  const colonnePagamenti: Colonna<typeof r.ultimi_pagamenti[0] & { id: string }>[] = [
    { chiave: 'data', intestazione: 'Data', render: (p) => formatDataOra(p.data) },
    { chiave: 'importo', intestazione: 'Importo', allineamento: 'right', render: (p) => <span className="mono" style={{ fontWeight: 600 }}>{formatEuro(p.importo)}</span> },
    { chiave: 'stato', intestazione: 'Stato', render: (p) => <Badge variante={p.stato === 'succeeded' ? 'success' : 'warning'}>{p.stato}</Badge> },
  ]

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Revenue</h1>
          <div className={pagina.sottotitolo}>Dati live da Stripe</div>
        </div>
      </div>

      <div className={pagina.griglia}>
        <StatCard etichetta="MRR" valore={formatEuro(r.mrr)} />
        <StatCard etichetta="ARR" valore={formatEuro(r.arr)} />
        <StatCard etichetta="Paganti attivi" valore={r.per_piano.reduce((s, x) => s + x.utenti, 0)} />
        <StatCard etichetta="Churn 30gg" valore={r.churn_ultimi_30_giorni.length} />
      </div>

      <Card titolo="Revenue per piano">
        {r.per_piano.length > 0 ? (
          <BarChart dati={r.per_piano.map(p => ({ ...p, label: p.piano }))} chiaveX="label" chiaveY="revenue" />
        ) : (
          <EmptyState titolo="Nessuna subscription attiva" />
        )}
      </Card>

      <div className={pagina.due}>
        <Card titolo="Ultimi pagamenti">
          <Table
            colonne={colonnePagamenti}
            righe={r.ultimi_pagamenti.map((p, i) => ({ ...p, id: `${i}` }))}
            vuoto={<EmptyState titolo="Nessun pagamento recente" />}
          />
        </Card>

        <Card titolo="Prossimi rinnovi">
          {r.prossimi_rinnovi.length === 0 ? (
            <EmptyState titolo="Nessun rinnovo previsto" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {r.prossimi_rinnovi.slice(0, 10).map((p, i) => (
                <div key={i} style={{
                  padding: 10, background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
                  fontSize: 13, color: 'var(--text-secondary)',
                }}>
                  {p.data ? formatDataOra(p.data) : '—'}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
